import { useMemo } from "react";

import { ChevronDownIcon } from "@vericus/cadmus-icons";
import { DropdownMenu, Text } from "@vericus/cadmus-ui";

import clsx from "clsx";

import { shuffleWithSeed } from "../../utils/shuffle-with-seed";
import * as styles from "./dropdown-choices.css";

interface DropdownProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Choices to display in the dropdown
   */
  choices: { identifier: string; content: string }[];

  /**
   * Seed to shuffle the choices
   */
  seed: string;

  /**
   * Whether to shuffle the choices
   * @default true
   */
  shuffle?: boolean;

  /**
   * Identifier of the selected choice
   */
  selectedChoiceId: string | null;

  /**
   * Callback when a choice is selected
   */
  onSelectChoice: (choiceId: string) => void;

  /**
   * Placeholder text for input
   */
  placeholder?: string;

  /**
   * Whether the dropdown is readonly
   * @default false
   */
  isReadonly?: boolean;

  /**
   * Whether to show a separator between choices
   * @default false
   */
  hasSeparator?: boolean;

  /**
   * Dropdown content width in px or auto.
   * @default 200
   */
  width?: number | "auto";
}

/**
 * Renders dropdown with choices to select as an answer.
 */
export function DropdownChoices(props: DropdownProps) {
  const {
    className,
    seed,
    shuffle = true,
    selectedChoiceId,
    placeholder,
    choices,
    isReadonly = false,
    hasSeparator = false,
    width = 200,
  } = props;

  const choicesToDisplay = useMemo(() => {
    if (!shuffle) return choices;
    return shuffleWithSeed(choices, seed);
  }, [shuffle, choices, seed]);

  const selectedChoice = choices.find(
    (choice) => choice.identifier === selectedChoiceId
  )?.content;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          className={clsx(styles.trigger, className)}
          disabled={isReadonly}
        >
          {selectedChoice ? (
            <Text kind="bodyMd" color="navy700" textAlign="left">
              {selectedChoice}
            </Text>
          ) : (
            <Text kind="bodyMd" color="grey500">
              {placeholder}
            </Text>
          )}
          <div className={styles.dropdownIcon}>
            <ChevronDownIcon label="" size={24} />
          </div>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content sideOffset={8} style={{ width }}>
        {choicesToDisplay.map((choice, index) => (
          <div key={index}>
            <DropdownMenu.CheckboxItem
              disabled={isReadonly}
              key={index}
              content={choice.content}
              tickable
              checked={selectedChoiceId === choice.identifier}
              onSelect={() => {
                if (selectedChoiceId === choice.identifier) {
                  return;
                } else {
                  props.onSelectChoice(choice.identifier);
                }
              }}
            />
            {hasSeparator && index < choices.length - 1 && (
              <DropdownMenu.Separator />
            )}
          </div>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}
